import {
  call,
  put,
  takeLatest,
  select,
} from 'redux-saga/effects';
import { get } from 'lodash';
import { message, notification } from 'antd';
import {
  FORM_TYPES,
  LIST_SCOPE,
  LIST_TYPES,
} from './types';
import * as actions from './actions';
import { formViewSelector, policyLookupSelector } from './selectors';
import { getSchedule, updateSchedule, deleteSchedule } from '@api/schedulePolicies';
import { autoRequest } from '@src/api/createRequestStore';
import { isScheduleViewSelector } from '../precisionPolicy/selectors';
import * as policyActions from '../precisionPolicy/actions';
import { genSummaryText, messageMap } from './utils';

const displayError = (err) => {
  const error = get(err, 'response.data.message', 'Unknown Error');
  message.error(error);
};

function* generateUpdateSummary(out) {
  // duration set to 0 means the notification
  // will not be closed automatically
  // useful for error/partial case
  const { level, text, duration = 0 } = messageMap[out.status];
  const policyLookup = yield select(policyLookupSelector);

  const description = genSummaryText(policyLookup, out);
  const config = {
    message: text,
    description: description.join(','),
    duration,
  }
  notification[level](config);
}

function* init() {
  yield put(actions.initForm());
  yield put(actions.initList());
}

function* saveRecord(action) {
  yield put(actions.setFormLoading(true));
  const isScheduleView = yield select(isScheduleViewSelector);
  const {
    activationState,
    notes,
    policies,
    scheduleId,
    triggerAt,
    emailIds,
  } = yield select(formViewSelector);

  try {
    const res = yield call(updateSchedule, {
      data: {
        activationState,
        notes,
        emailIds,
        policies,
        scheduleId,
        triggerAt,
      },
    });

    // check for errors
    if ( activationState === 'now') {
      yield generateUpdateSummary(res.data);
    } else {
      notification.success({
        message: `Schedule ${scheduleId ? 'updated' : 'saved'} successfully.`,
      });
    }
    // done, close and reset form
    yield call(action.doneCallback);

    // reload list/calendar view
    if (isScheduleView) {
      yield put(actions.fetchListData());
    } else {
      yield put(policyActions.init());
    }
  } catch (err) {
    displayError(err);
    // allow user to try saving again
    yield put(actions.setFormLoading(false));
  }
}

function* deleteRecord(action) {
  yield put(actions.setFormLoading(true));
  const { scheduleId } = yield select(formViewSelector);
  try {
    // TODO: check response and update message
    yield call(deleteSchedule, scheduleId);
    // done, close and reset form
    yield call(action.doneCallback);
    yield put(actions.fetchListData());
    message.success(`Schedule deleted successfully.`);
  } catch (err) {
    displayError(err);
    yield put(actions.setFormLoading(false));
  }
}

function* fetchList() {
  yield put(actions.fetchingList());
  try {
    yield autoRequest(getSchedule, LIST_SCOPE);
  } catch (error) {
    const errorMsg = error.message || 'Unknown error!';
    message.error(errorMsg);
  }
}

const watchMap = {
  [FORM_TYPES.SAVE_SCHEDULE]: saveRecord,
  [FORM_TYPES.DELETE_SCHEDULE]: deleteRecord,
  [LIST_TYPES.FETCH_DATA]: fetchList,
};

function* watch() {
  yield init();
  for (const [type, func] of Object.entries(watchMap)) {
    yield takeLatest([type], func);
  }
}

export default [
  watch,
];
